import { memo, useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { SPREAD_METHOD_PAD } from '../constants';
import { AngularGradientFill } from '../types';
import { angleToPoint, createGetColor, normalizeGradientStops } from '../utils';

type Props = AngularGradientFill['props'] & {
  id: string;
  to?: string;
  from?: string;
};

const AngularGradientPolygon = ({
  angle = 0,
  from = '',
  id,
  scale = 1,
  slices = 15,
  stops,
  spreadMethod = SPREAD_METHOD_PAD,
  to = '',
}: Props) => {
  const gradientStops = useMemo(() => normalizeGradientStops(from, to, stops), [from, stops, to]);

  const polygons = useMemo(() => {
    const getColor = createGetColor(gradientStops, scale, spreadMethod);
    const polygonsArray: { key: number; fill: string; points: string }[] = [];
    for (let i = 0; i < slices; i += 1) {
      const color = getColor(i / (slices - 1)).map((c, j) => (j < 3 ? Math.floor(c) : c / 255));
      const start = i / slices;
      const end = i === slices - 1 ? (i + 1) / slices : (i + 2) / slices;
      polygonsArray.push({
        key: start,
        fill: `rgba(${color.join(',')})`,
        points: [angleToPoint(360 * start + angle), angleToPoint(360 * end + angle), [0.5, 0.5]]
          .map((point) => point.join(','))
          .join(','),
      });
    }
    return polygonsArray;
  }, [angle, scale, spreadMethod, slices, gradientStops]);

  return (
    <pattern
      height={1}
      id={id}
      patternUnits="objectBoundingBox"
      patternContentUnits="objectBoundingBox"
      width={1}
    >
      {polygons.map(({ key, fill, points }) => (
        <polygon key={key} fill={fill} points={points} />
      ))}
    </pattern>
  );
};

export default memo(AngularGradientPolygon, (props, nextProps) => isEqual(props, nextProps));
